import React, { useEffect, useState } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { Lock, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { getApiKey } from '@/lib/api';

// Read the live tier matrix from /api/tiers/{currentTier} and render the user a complete
// per-tier feature view: every feature unlocked at their tier is shown as available; every
// feature gated above their tier is shown with a lock + the tier required to unlock it.
//
// This replaces the prior "trust us, the engine has 45 features" implicit claim with a
// visible, per-feature, tier-aware surface. A free-tier user immediately sees what they get
// + what they could upgrade to access. A Strategist (admin) user sees everything unlocked
// as confirmation of full access.

interface Feature {
  id: string;
  category: string;
  label: string;
  minTier: string;
}

interface TierResponse {
  tier: string;
  features: Feature[];
  nextTier: string | null;
  unlocksAtNext: Feature[];
  featureCount: number;
}

const TIER_ORDER = ['Evaluator', 'Analyst', 'Builder', 'Operator', 'Strategist'];

async function fetchMyTierMatrix(): Promise<TierResponse | null> {
  // /api/tiers/me resolves the current user's tier from the auth middleware's req.user.maxTier,
  // translates internal->public tier name, and returns the full tier matrix for that user.
  // Single round-trip, no client-side tier inference needed.
  try {
    const key = getApiKey();
    const headers: Record<string, string> = {};
    if (key) headers['x-api-key'] = key;
    const res = await fetch('/api/tiers/me', { headers });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function TierFeatureGrid() {
  const [tier, setTier] = useState<string>('Evaluator');
  const [data, setData] = useState<TierResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const m = await fetchMyTierMatrix();
      if (m) {
        setTier(m.tier);
        setData(m);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return <div className="text-[#A8A29E] text-sm">Loading tier matrix...</div>;
  }
  if (!data) {
    return <div className="text-[#A8A29E] text-sm">Tier matrix unavailable.</div>;
  }

  // Group unlocked features by category
  const unlockedByCat: Record<string, Feature[]> = {};
  for (const f of data.features) {
    const bucket = unlockedByCat[f.category] ?? (unlockedByCat[f.category] = []);
    bucket.push(f);
  }

  // Group locked-at-next features by category (only the immediate-next-tier unlocks)
  const lockedByCat: Record<string, Feature[]> = {};
  for (const f of data.unlocksAtNext) {
    const bucket = lockedByCat[f.category] ?? (lockedByCat[f.category] = []);
    bucket.push(f);
  }

  const tierIdx = TIER_ORDER.indexOf(tier);
  const isTopTier = data.nextTier === null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            Your tier: <span style={{ color: GovernanceColors.primary }}>{tier}</span>
            <span className="text-xs text-[#A8A29E]">({data.features.length} of 45 features unlocked)</span>
          </h3>
          <p className="text-xs text-[#A8A29E] mt-1">
            {isTopTier
              ? 'You have access to every feature in the platform.'
              : `Upgrade to ${data.nextTier} to unlock ${data.unlocksAtNext.length} additional feature${data.unlocksAtNext.length === 1 ? '' : 's'}.`}
          </p>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {TIER_ORDER.map((t, i) => (
            <span
              key={t}
              className={`px-2 py-1 rounded ${
                i === tierIdx
                  ? 'text-white font-medium'
                  : i < tierIdx
                  ? 'bg-[#292524] border'
                  : 'bg-[#292524] text-[#8F857B] border border-[#3A352F]'
              }`}
              style={
                i === tierIdx
                  ? { backgroundColor: GovernanceColors.primary }
                  : i < tierIdx
                  ? { color: GovernanceColors.healthy, borderColor: `${GovernanceColors.healthy}4D` }
                  : undefined
              }
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Unlocked features by category */}
      <div>
        <div className="text-xs uppercase tracking-wider mb-2 flex items-center gap-1" style={{ color: GovernanceColors.healthy }}>
          <CheckCircle2 className="w-3 h-3" /> Unlocked at {tier}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {Object.entries(unlockedByCat).map(([cat, feats]) => (
            <div key={cat} className="bg-[#1C1917] border border-[#2E2A26] rounded p-3">
              <div className="text-xs font-semibold mb-2" style={{ color: GovernanceColors.primaryLight }}>{cat}</div>
              <ul className="space-y-1">
                {feats.map(f => (
                  <li key={f.id} className="text-xs text-[#C9D1D9] flex items-start gap-1.5">
                    <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" style={{ color: GovernanceColors.healthy }} />
                    <span>{f.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Locked features at next tier */}
      {!isTopTier && data.unlocksAtNext.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider mb-2 flex items-center gap-1" style={{ color: GovernanceColors.warning }}>
            <Lock className="w-3 h-3" /> Unlocks at {data.nextTier}
            <a
              href="https://www.agentomy.com/install.html"
              rel="noopener"
              className="ml-auto inline-flex items-center gap-1 text-[var(--gc-primary)] hover:text-[var(--gc-primary-light)] text-[10px] normal-case tracking-normal"
              style={{ '--gc-primary': GovernanceColors.primary, '--gc-primary-light': GovernanceColors.primaryLight } as React.CSSProperties}
            >
              Upgrade to {data.nextTier} <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(lockedByCat).map(([cat, feats]) => (
              <div key={cat} className="bg-[#1C1917] border rounded p-3 opacity-75" style={{ borderColor: `${GovernanceColors.warning}4D` }}>
                <div className="text-xs font-semibold mb-2" style={{ color: GovernanceColors.warning }}>{cat}</div>
                <ul className="space-y-1">
                  {feats.map(f => (
                    <li key={f.id} className="text-xs text-[#A8A29E] flex items-start gap-1.5">
                      <Lock className="w-3 h-3 mt-0.5 shrink-0" style={{ color: GovernanceColors.warning }} />
                      <span>{f.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
